// @ts-nocheck
import axios from 'axios'

const baseURL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:4000/api/v1' : '/api/v1')

export const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Track in-flight refresh to avoid duplicate calls
let refreshPromise: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) throw new Error('No refresh token')

  const response = await axios.post(`${baseURL}/auth/refresh`, { refreshToken })
  const { token, refreshToken: newRefreshToken } = response.data

  localStorage.setItem('token', token)
  if (newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken)
  }

  return token
}

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      try {
        // Deduplicate concurrent refresh calls
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null
          })
        }
        const newToken = await refreshPromise
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        refreshPromise = null
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        window.dispatchEvent(new Event('auth:logout'))
      }
    }
    
    return Promise.reject(error)
  }
)

// Proactive token refresh: refresh the access token before it expires
// so active users never hit a 401
function scheduleProactiveRefresh() {
  const token = localStorage.getItem('token')
  if (!token) return

  try {
    // Decode JWT payload (base64) without verification
    const payload = JSON.parse(atob(token.split('.')[1]))
    const expiresAt = payload.exp * 1000 // ms
    // Refresh 2 minutes before expiry
    const refreshAt = expiresAt - Date.now() - 2 * 60 * 1000

    if (refreshAt <= 0) return // Already expired or too close

    setTimeout(async () => {
      // Only refresh if the token in storage is still the same (user hasn't logged out)
      if (localStorage.getItem('token') !== token) return
      try {
        await refreshAccessToken()
        scheduleProactiveRefresh() // Schedule next refresh
      } catch {
        // Silent fail — the reactive interceptor will handle it on next request
      }
    }, refreshAt)
  } catch {
    // Invalid token format — ignore
  }
}

// Start proactive refresh on load and whenever token changes
scheduleProactiveRefresh()
window.addEventListener('storage', (e) => {
  if (e.key === 'token' && e.newValue) {
    scheduleProactiveRefresh()
  }
})
window.addEventListener('auth:token-updated', () => {
  scheduleProactiveRefresh()
})